import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChange } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Subscription, take } from 'rxjs';
import { Settings } from 'src/app/core/models/settings.model';
import { Entity } from 'src/app/core/models/entity.model';
import { Groups } from 'src/app/core/models/group.model';
import { SettingsService } from 'src/app/core/services/settings.service';
import { User } from 'src/app/core/models/user.model';
import * as utils from 'src/app/shared/helpers/utils';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ConfigService } from 'src/app/core/services/config.service';
import { EntityService } from 'src/app/core/services/entity.service';
import { FileSpecsService } from 'src/app/core/services/file-specs.service';

@Component({
  selector: 'hbo-setting-backoffice',
  templateUrl: './setting-backoffice.component.html',
  styleUrls: ['./setting-backoffice.component.scss']
})
export class SettingBackofficeComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() entity!: Entity;
  @Input() entityId!: number;
  @Input() isOpen = false;
  @Input('row') row!: number;
  @Input() entityApps: number[] = [];
  // Apps the entity reaches through a package that ships nothing else. The dashboard cards
  // are configured for the standalone product, so a Fit Depict entity must not see the
  // 360 Depict block even though its `entityApps` contains app 7.
  @Input() standaloneAppsIds: number[] = [];
  @Output() reload: EventEmitter<any> = new EventEmitter();
  @Output() setApps: EventEmitter<any> = new EventEmitter();

  private readonly groups = new Groups();

  mimesysAvailable = false;
  fitAvailable = false;
  depict360Available = false;


  // Unica fuente de verdad para editar settings: la consultan tanto getSettings() para
  // deshabilitar el formulario como la plantilla para mostrar los botones de guardado.
  get canEditSettings(): boolean {
    if (this.user.profileId === 2) return false;
    if (this.entity.id !== this.user.entityId) return true;
    // Sobre la propia entidad solo edita el owner, salvo el editor de optica
    return this.user.profileId === 3
      || (this.user.roleId === 4 && this.user.profileId === 1);
  }

  get isFormReadOnly(): boolean {
    return !this.canEditSettings;
  }

  // A head office without the grant has no dashboard of its own: what it resolves through
  // inheritance belongs to the distributor, so there is nothing to show at this level.
  get hasOwnDashboardConfig(): boolean {
    return this.entity?.entityTypeId !== utils.ENTITY_TYPE_HEAD_OFFICE
      || !!this.currentSettings?.dashboard?.allowConfigure;
  }

  get showDashboardSection(): boolean {
    return this.entity?.entityTypeId !== utils.ENTITY_TYPE_OPTICAL
      && this.groups.showDashboard({ appsIds: this.entityApps, standaloneAppsIds: this.standaloneAppsIds })
      && this.hasOwnDashboardConfig;
  }

  // The grant lives on the distributor: any admin sees it, and so does the distributor
  // owner over its own entity. Nobody else, since nobody else can concede it.
  get showAllowConfigure(): boolean {
    if (this.entity?.entityTypeId !== utils.ENTITY_TYPE_DISTRIBUTOR) return false;
    return this.user.roleId === utils.ROLE_ADMIN
      || (this.user.roleId === utils.ROLE_DISTRIBUTOR && this.user.profileId === utils.PROFILE_OWNER);
  }

  get canToggleAllowConfigure(): boolean {
    return this.showAllowConfigure && this.canEditSettings;
  }

  // The dashboard follows the same permission as the rest of the settings, with a single
  // deviation: a distributor editor only ever views it, on its own entity and on its
  // children's. The entity side of the rule already lives in showDashboardSection.
  get canEditDashboard(): boolean {
    if (!this.showDashboardSection) return false;
    if (!this.canEditSettings) return false;
    return !(this.user.roleId === utils.ROLE_DISTRIBUTOR
      && this.user.profileId === utils.PROFILE_EDITOR);
  }

  get dashboardPreviewThumbnail(): string | null {
    const cards = [this.mimesysAvailable, this.fitAvailable, this.depict360Available].filter(Boolean).length;
    return cards ? `assets/images/preview/dashboard-cards-${cards}.png` : null;
  }

  // <input type="color"> has no empty state: a null control is normalised to #000000 and painted
  // black, while the card itself falls back to the Horizons palette. The picker shows that same
  // fallback without writing it to the control, so an untouched colour is still saved as null.
  dashboardColorPickerValue(controlName: string, fallbackColor: string): string {
    return this.settingsForm.get(controlName)?.value ?? fallbackColor;
  }

  // Replaces what formControlName resolved on its own: the value binding leaves the control's
  // disabled state unwired, and the permissions map still drives it.
  isDashboardColorDisabled(controlName: string): boolean {
    return !!this.settingsForm.get(controlName)?.disabled;
  }

  onDashboardColorPicked(controlName: string, event: Event): void {
    this.settingsForm.get(controlName)?.setValue((event.target as HTMLInputElement).value);
  }

  extensions = ['png', 'jpg', 'jpeg', 'PNG', 'JPG', 'JPEG', 'svg', 'SVG'];
  fileExtError: { [key: string | number]: any } = {};

  maxFileSize: number = 100000;
  maxFileSizeExtra: number = 1500000;
  maxFileSizeExceed: { [key: string | number]: any } = {};
  badFileNaming: { [key: string | number]: any } = {};

  actualFilenames: { [key: string]: string } = {};

  public utils = utils;

  user: User = utils?.localStorageGetItem('user');

  previousSettings?: Settings;
  showSettings = false;
  isDashboardPreviewVisible = false;
  currentSettings?: Settings;


  loginBackgroundImage: string = '';
  sideBarBackgroundImage: string = '';

  resPersistance: any;

  logos: string[] = ['logo1', 'logo2', 'logo3', 'logo4', 'logo5', 'logo6'];

  settingsForm = this.formBuilder.nonNullable.group({
    language: ['es-ES'],
    loginToken: ['modelo'],
    distributorDomain: [''],
    distributorDomainAllow: [false],
    fromMail: ['', Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)],//the Validators.email is conform The WHATWG and is not complet for example about end . sintax
    fromMailAllow: [false],
    sendPasswordEmail: [true],
    sendPasswordEmailConfigurable: [false],
    modalPosition: ['center'],
    modalColor: ['black'],
    header: ['black'],
    wallpaperBg: ['color'],
    wallpaperColor: ['black'],
    wallpaperImage: [''],
    logo1: [''],
    logo2: [''],
    logo3: [''],
    sidebarBg: ['color'],
    sidebarColor: ['black'],
    sidebarImage: [''],
    sidebarFontColor: ['white'],
    logo4: [''],
    logo4Allow: [false],
    logo5: [''],
    logo5Allow: [false],
    logo6: [''],
    logo6Allow: [false],
    dashboardCardHeaderColor: [null as string | null],
    dashboardCardOutlineColor: [null as string | null],
    dashboardCardMimesysHeaderLogo: [''],
    dashboardCardMimesysBodyImage: [''],
    dashboardCardFitHeaderLogo: [''],
    dashboardCardFitBodyImage: [''],
    dashboardCardDepict360HeaderLogo: [''],
    dashboardCardDepict360BodyImage: [''],
    dashboardAllowConfigure: [false],
  });

  login_svg_thumbnail!: SafeResourceUrl;
  login_layout_lstnr: Subscription = new Subscription();
  sidebar_svg_layout!: SafeResourceUrl;
  sidebar_layout_lstnr: Subscription = new Subscription();
  svgTextOriginal!: string;
  svgText!: string;
  sidebarSvgTextOriginal!: string;
  sidebarSvgText!: string;
  svgUrl!: string;
  svgUrlBefore: string = '';
  svgSidebarUrl!: string;
  svgSidebaUrlBefore: string = '';

  fileNames?: { [key: string | number]: any } = {}
  constructor(
    private sanitize: DomSanitizer,
    private formBuilder: FormBuilder,
    private settingsService: SettingsService,
    private configService: ConfigService,
    private entityService: EntityService,
    private fileSpecsService: FileSpecsService
  ) {
    this.login_layout_lstnr = this.settingsForm.valueChanges.subscribe(data => {
      this.svgUrl = 'assets/images/preview/login_' + this.settingsForm.get('modalPosition')?.value! + '_' + this.settingsForm.get('modalColor')?.value! + '_' + this.settingsForm.get('header')?.value! + '.svg';
      if (this.svgUrlBefore !== this.svgUrl) {
        this.svgUrlBefore = this.svgUrl;
        fetch('assets/images/preview/login_' + this.settingsForm.get('modalPosition')?.value! + '_' + this.settingsForm.get('modalColor')?.value! + '_' + this.settingsForm.get('header')?.value! + '.svg')
          .then(response => response.text())  // Fetch the SVG content as text
          .then(svgText => {
            this.svgTextOriginal = svgText;
            this.renderLoginLayout(this.settingsForm.get('wallpaperColor')?.value!);
          })
          .catch(error => {
            console.error("Error fetching SVG:", error);
          });
      } else {
        this.renderLoginLayout(this.settingsForm.get('wallpaperColor')?.value!);
      }
    });
    this.sidebar_layout_lstnr = this.settingsForm.valueChanges.subscribe(data => {
      this.svgSidebarUrl = 'assets/images/preview/layout.svg';
      if (this.svgSidebaUrlBefore !== this.svgSidebarUrl) {
        this.svgSidebaUrlBefore = this.svgSidebarUrl;
        fetch('assets/images/preview/layout.svg')
          .then(response => response.text())  // Fetch the SVG content as text
          .then(svgText => {
            this.sidebarSvgTextOriginal = svgText;
            this.renderSidebarLayout(this.settingsForm.get('sidebarColor')?.value!);
          })
          .catch(error => {
            console.error("Error fetching SVG:", error);
          });
      } else {
        this.renderSidebarLayout(this.settingsForm.get('sidebarColor')?.value!);
      }
    })

  }
  ngOnInit() {

  }
  sanitizeText(txt: string): SafeResourceUrl {
    return this.sanitize.bypassSecurityTrustHtml(txt);
  }

  sanitizeUrl(url: string): SafeResourceUrl {
    return this.sanitize.bypassSecurityTrustResourceUrl(url);
  }

  ngAfterViewInit(): void {
    const layoutThumbnail = document.querySelector('#layoutThumbnail-' + this.entity.id) as HTMLObjectElement;
    const loginThumbnailLeft = document.querySelector('#loginThumbnailLeft-' + this.entity.id) as HTMLObjectElement;
    const loginThumbnailCenter = document.querySelector('#loginThumbnailCenter-' + this.entity.id) as HTMLObjectElement;
    const loginThumbnailRight = document.querySelector('#loginThumbnailRight-' + this.entity.id) as HTMLObjectElement;

    this.settingsForm.get('sidebarColor')?.valueChanges.subscribe(value => {
      /*layoutThumbnail?.contentDocument?.querySelector('rect:nth-of-type(' + 2 + ')')?.setAttribute('fill', value);*/
      this.renderSidebarLayout(value);
    });

    this.settingsForm.get('sidebarFontColor')?.valueChanges.subscribe(value => {
      for (let i = 6; i <= 12; i++) {
        layoutThumbnail?.contentDocument?.querySelector('rect:nth-of-type(' + i + ')')?.setAttribute('fill', value);
      }
    });

    this.settingsForm.get('modalColor')?.valueChanges.subscribe(value => {
      loginThumbnailLeft?.contentDocument?.querySelector('rect:nth-of-type(' + 2 + ')')?.setAttribute('fill', value);
      loginThumbnailCenter?.contentDocument?.querySelector('rect:nth-of-type(' + 7 + ')')?.setAttribute('fill', value);
      loginThumbnailRight?.contentDocument?.querySelector('rect:nth-of-type(' + 7 + ')')?.setAttribute('fill', value);
    });

    this.settingsForm.get('header')?.valueChanges.subscribe(value => {
      loginThumbnailLeft?.contentDocument?.querySelector('rect:nth-of-type(' + 9 + ')')?.setAttribute('fill', value);
      loginThumbnailCenter?.contentDocument?.querySelector('rect:nth-of-type(' + 2 + ')')?.setAttribute('fill', value);
      loginThumbnailRight?.contentDocument?.querySelector('rect:nth-of-type(' + 2 + ')')?.setAttribute('fill', value);
    });

    this.settingsForm.get('wallpaperColor')?.valueChanges.subscribe(value => {
      this.renderLoginLayout(value);
    });
  }

  renderLoginLayout = (_value: string = 'transparent') => {
    this.svgText = this.svgTextOriginal;
    if (this.settingsForm.controls.wallpaperBg.value === 'image' && (this.currentSettings!.login.wallpaper.backgroundImage || this.settingsForm.get('wallpaperImage')!.value.length)) {
      _value = 'transparent';
      this.svgText?.replace(/fill="[^"]*"/g, 'fill="transparent"');
    } else {
      this.loginBackgroundImage = _value;
    }
    this.svgText = this.replaceSecondFill(this.svgText, _value, 2);
    const encodedSvg = encodeURIComponent(this.svgText);
    this.login_svg_thumbnail = this.sanitizeUrl(`data:image/svg+xml;charset=UTF-8,${encodedSvg}`);
  }

  renderSidebarLayout = (_value: string = 'transparent') => {
    this.sidebarSvgText = this.sidebarSvgTextOriginal;
    if (this.settingsForm.controls.sidebarBg.value === 'image' && (this.currentSettings!.layout.sidebar.backgroundImage || this.settingsForm.get('sidebarImage')!.value.length)) {
      _value = 'transparent';
      this.sidebarSvgText = this.replaceSecondFill(this.sidebarSvgText, _value, 1);
      this.sidebarSvgText = this.replaceSecondFill(this.sidebarSvgText, _value, 2);
      this.sidebarSvgText?.replace(/fill="[^"]*"/g, 'fill="transparent"');
    }
    this.sidebarSvgText = this.replaceSecondFill(this.sidebarSvgText, _value, 3);
    const encodedSvg = encodeURIComponent(this.sidebarSvgText);
    this.sidebar_svg_layout = this.sanitizeUrl(`data:image/svg+xml;charset=UTF-8,${encodedSvg}`);

  }

  replaceSecondFill = (svgString: string, newFillValue: string, rect: number) => {
    // Regular expression to match all 'fill="..."' attributes
    const fillPattern = /fill="([^"]*)"/g;
    let count = 0;
    let updatedSvgString = svgString;
    // Use regular expression to find all occurrences of 'fill="..."'
    updatedSvgString = updatedSvgString?.replace(fillPattern, (match, group) => {
      count++; // Increment count for each match
      // Replace the second occurrence of 'fill' with the new value
      if (count === rect) {
        return `fill="${newFillValue}"`; // Replace second match
      }
      // Return the original match for other occurrences
      return match;
    });
    return updatedSvgString;
  }

  ngOnChanges(changes: { [property: string]: SimpleChange }) {
    if (changes['entityApps'] || changes['standaloneAppsIds']) {
      this.mimesysAvailable = this.entityApps.some(id => this.groups.mimesys.includes(id));
      // app ids, not packageType ids: app 5 = FIT, app 7 = 360Depict
      this.fitAvailable = this.entityApps.includes(5);
      // Mirrors the dashboard card: only the standalone product has a card to configure.
      this.depict360Available = this.standaloneAppsIds.includes(7);
      // The apps land in their own request, and the dashboard permissions depend on them.
      // If the settings resolved first the controls were disabled with no apps known, so
      // the enable/disable pass has to run again now that they are.
      if (this.currentSettings) this.applyUserCreationAndMailSenderPermissions();
    }
    if (changes['isOpen']?.currentValue) {
      this.showSettings = false;
      this.settingsService.get(this.entity.id, 0).subscribe(res => {
        this.getSettings(res);
        this.configService.setConfigSettingsValue('settings', this.entity.id, 'backOffice', res);
      });
    }
  }

  refreshSettings = (e: Event) => {
    this.settingsService.get(this.entity.id, 0).subscribe(res => {
      this.getSettings(res);
      this.configService.setConfigSettingsValue('settings', this.entity.id, 'backOffice', res);
    });
  }


  ngOnDestroy(): void {
    this.login_layout_lstnr.unsubscribe();
    if (this.previousSettings) {
      this.settingsService.settingsStore.next(this.previousSettings);
    }
  }

  getSettings = (_settings: Settings) => {
    this.showSettings = true;
    this.setForm(_settings);
    this.resPersistance = JSON.parse(JSON.stringify(_settings));
    this.renderLoginLayout(this.settingsForm.get('wallpaperColor')?.value!);
    this.loginBackgroundImage = 'url(' + _settings.login!.wallpaper!.backgroundImage! + '?' + new Date().getTime() + ')';
    this.sideBarBackgroundImage = 'url(' + _settings.layout.sidebar.backgroundImage! + '?' + new Date().getTime() + ')';

    if (!this.canEditSettings) {
      this.settingsForm.disable();
    }

    this.applyUserCreationAndMailSenderPermissions();
  }

  applyUserCreationAndMailSenderPermissions = () => {
    if (this.settingsForm.disabled) {
      return;
    }

    const canEditByOwner = this.user.roleId === 1 && this.user.profileId === 3;
    const canEditSendPasswordEmail = canEditByOwner && !(this.entity.entityTypeId === 3 && !this.currentSettings?.sendPasswordEmailConfigurable);
    const canEditFromMail = canEditByOwner && !(this.entity.entityTypeId === 3 && !this.currentSettings?.fromMailConfigurable);
    const canEditAllowCheckboxes = canEditByOwner && this.entity.entityTypeId === 2;

    const permissionsMap: { [key: string]: boolean } = {
      sendPasswordEmail: canEditSendPasswordEmail,
      fromMail: canEditFromMail,
      sendPasswordEmailConfigurable: canEditAllowCheckboxes,
      fromMailAllow: canEditAllowCheckboxes,
      dashboardCardHeaderColor: this.canEditDashboard,
      dashboardCardOutlineColor: this.canEditDashboard,
      dashboardCardMimesysHeaderLogo: this.canEditDashboard,
      dashboardCardMimesysBodyImage: this.canEditDashboard,
      dashboardCardFitHeaderLogo: this.canEditDashboard,
      dashboardCardFitBodyImage: this.canEditDashboard,
      dashboardCardDepict360HeaderLogo: this.canEditDashboard,
      dashboardCardDepict360BodyImage: this.canEditDashboard,
      dashboardAllowConfigure: this.canToggleAllowConfigure
    };

    Object.entries(permissionsMap).forEach(([controlName, canEdit]) => {
      const control = this.settingsForm.get(controlName);
      if (!control) {
        return;
      }

      if (canEdit) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });
  }

  cancel() {
    this.getSettings(this.resPersistance!);
    this.configService.setConfigSettingsValue('settings', this.entity.id, 'backOffice', this.resPersistance!);
    this.ngOnDestroy();
  }

  toBase64(fileInput: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const file = (document.querySelector('#' + fileInput + '-' + this.entity.id) as HTMLInputElement).files?.[0];
      if (file) {
        reader.readAsDataURL(file as Blob);
        reader.onload = () => resolve(reader.result?.toString() ?? '');
        reader.onerror = reject;
      } else {
        resolve('');
      }
    });
  }

  async preview(component: string) {
    this.settingsService.settingsStore.pipe(take(1)).subscribe(res => {
      res['isPreview'] = true;
      this.previousSettings = res;
    });
    if (component === 'login') {
      this.settingsService.settingsStore.next({
        ...this.previousSettings,
        language: this.settingsForm.get('language')?.value,
        login: {
          form: {
            position: this.settingsForm.get('modalPosition')?.value as 'left' | 'center' | 'right',
            backgroundColor: this.settingsForm.get('modalColor')?.value as 'black' | 'white'
          },
          header: {
            backgroundColor: this.settingsForm.get('header')?.value as 'transparent' | 'black' | 'white'
          },
          wallpaper: {
            background: !this.currentSettings?.login.wallpaper.backgroundImage.length ? 'color' : this.settingsForm.get('wallpaperBg')?.value as 'color' | 'image',
            backgroundColor: this.settingsForm.get('wallpaperColor')?.value ?? '',
            backgroundImage: this.settingsForm.get('wallpaperImage')?.value ? await this.toBase64('wallpaperImage') : this.currentSettings?.login.wallpaper.backgroundImage,
          }
        },
        logo: {
          ...this.previousSettings?.logo,
          1: {
            image: this.settingsForm.get('logo1')?.value ? await this.toBase64('logo1') : this.currentSettings?.logo[1].image,
            configurable: false
          },
          2: {
            image: this.settingsForm.get('logo2')?.value ? await this.toBase64('logo2') : this.currentSettings?.logo[2].image,
            configurable: false
          },
          3: {
            image: this.settingsForm.get('logo3')?.value ? await this.toBase64('logo3') : this.currentSettings?.logo[3].image,
            configurable: false
          }
        }
      } as Settings);

    } else if (component === 'layout') {
      this.settingsService.settingsStore.next({
        ...this.previousSettings,
        language: this.settingsForm.get('language')?.value,
        layout: {
          sidebar: {
            background: !this.currentSettings?.layout.sidebar.backgroundImage.length ? 'color' : this.settingsForm.get('sidebarBg')?.value as 'color' | 'image',
            color: this.settingsForm.get('sidebarFontColor')?.value as 'black' | 'white',
            backgroundColor: this.settingsForm.get('sidebarColor')?.value ?? '',
            backgroundImage: this.settingsForm.get('sidebarImage')?.value ? await this.toBase64('sidebarImage') : this.currentSettings?.layout.sidebar.backgroundImage,
          }
        },
        logo: {
          ...this.previousSettings?.logo,
          4: {
            image: this.settingsForm.get('logo4')?.value ? await this.toBase64('logo4') : this.currentSettings?.logo[4].image,
            configurable: this.settingsForm.get('logo4Allow')?.value as boolean
          },
          5: {
            image: this.settingsForm.get('logo5')?.value ? await this.toBase64('logo5') : this.currentSettings?.logo[5].image,
            configurable: this.settingsForm.get('logo5Allow')?.value as boolean
          },
          6: {
            image: this.settingsForm.get('logo6')?.value ? await this.toBase64('logo6') : this.currentSettings?.logo[6].image,
            configurable: this.settingsForm.get('logo6Allow')?.value as boolean
          }
        }
      } as Settings);
    }
  }

  previewDashboardCards(): void {
    this.isDashboardPreviewVisible = true;
  }

  async previewLogin() {
    this.preview('login');
    const loginPreview = document.querySelector('#loginPreview');
    loginPreview?.removeAttribute('hidden');
    loginPreview?.addEventListener('click', () => {
      loginPreview?.setAttribute('hidden', 'true');
    });
  }

  async submit() {
    const update = await this.setUpdate();

    this.settingsService.update(this.entity.id, 0, update).subscribe(res => {
      this.configService.currentConfigValue.paginations!['settings'].settings![this.entity.id]['backOffice'] = res;
      if (this.entity.id === this.user.entityId) {
        this.configService.setConfigSettingsValue('settings', this.entity.id, 'backOffice', res);
        this.renderLoginLayout(this.settingsForm.get('wallpaperColor')?.value!);
        this.preview('layout');
      } else {
        this.renderLoginLayout(this.settingsForm.get('wallpaperColor')?.value!);
      };
      /*this.loginBackgroundImage = 'url('+res.login!.wallpaper!.backgroundImage! + '?' + new Date().getTime()+')';
      this.sideBarBackgroundImage = 'url('+res.layout.sidebar.backgroundImage! + '?' + new Date().getTime()+')';*/
      this.resPersistance = JSON.parse(JSON.stringify(res));
    })
  }

  async setUpdate() {
    const update = {
      language: this.settingsForm.value.language,
      distributorDomain: this.user.roleId === 1 ? this.settingsForm.value.distributorDomain : null,
      distributorDomainConfigurable: this.user.roleId === 1 ? this.settingsForm.value.distributorDomainAllow : null,
      fromMail: this.user.roleId === 1 && this.user.profileId === 3 ? this.settingsForm.value.fromMail : null,
      fromMailConfigurable: this.user.roleId === 1 && this.user.profileId === 3 ? this.settingsForm.value.fromMailAllow : null,
      sendPasswordEmail: this.user.roleId === 1 && this.user.profileId === 3 ? this.settingsForm.value.sendPasswordEmail : null,
      sendPasswordEmailConfigurable: this.user.roleId === 1 && this.user.profileId === 3 ? this.settingsForm.value.sendPasswordEmailConfigurable : null,
      login: {
        form: {
          position: this.settingsForm.value.modalPosition,
          backgroundColor: this.settingsForm.value.modalColor,
        },
        header: {
          backgroundColor: this.settingsForm.value.header,
        },
        wallpaper: utils.cleanParams({
          background: this.settingsForm.value.wallpaperBg,
          backgroundColor: this.settingsForm.value.wallpaperColor,
          backgroundImage: this.settingsForm.get('wallpaperImage')?.value ? await this.toBase64('wallpaperImage') : this.currentSettings?.login.wallpaper.backgroundImage,
          name: this.actualFilenames['wallpaperImage']
        })
      },
      layout: {
        sidebar: utils.cleanParams({
          background: this.settingsForm.value.sidebarBg,
          color: this.settingsForm.value.sidebarFontColor,
          backgroundColor: this.settingsForm.value.sidebarColor,
          backgroundImage: this.settingsForm.get('sidebarImage')?.value ? await this.toBase64('sidebarImage') : this.currentSettings?.layout.sidebar.backgroundImage,
          name: this.actualFilenames['sidebarImage']
        })
      },
      logo: {
        1: utils.cleanParams({
          image: this.settingsForm.get('logo1')?.value ? await this.toBase64('logo1') : this.currentSettings?.logo[1].image,
          configurable: false,
          name: this.actualFilenames['logo1']
        }),
        2: utils.cleanParams({
          image: this.settingsForm.get('logo2')?.value ? await this.toBase64('logo2') : this.currentSettings?.logo[2].image,
          configurable: false,
          name: this.actualFilenames['logo2']
        }),
        3: utils.cleanParams({
          image: this.settingsForm.get('logo3')?.value ? await this.toBase64('logo3') : this.currentSettings?.logo[3].image,
          configurable: false,
          name: this.actualFilenames['logo3']
        }),
        4: utils.cleanParams({
          image: this.settingsForm.get('logo4')?.value ? await this.toBase64('logo4') : this.currentSettings?.logo[4].image,
          configurable: this.settingsForm.value.logo4Allow ?? false,
          name: this.actualFilenames['logo4']
        }),
        5: utils.cleanParams({
          image: this.settingsForm.get('logo5')?.value ? await this.toBase64('logo5') : this.currentSettings?.logo[5].image,
          configurable: this.settingsForm.value.logo5Allow ?? false,
          name: this.actualFilenames['logo5']
        }),
        6: utils.cleanParams({
          image: this.settingsForm.get('logo6')?.value ? await this.toBase64('logo6') : this.currentSettings?.logo[6].image,
          configurable: this.settingsForm.value.logo6Allow ?? false,
          name: this.actualFilenames['logo6']
        })
      },
      dashboard: await this.buildDashboardPayload()
    }

    return update
  }

  // Without edit permission the block is not sent at all: the GET resolves the dashboard
  // through inheritance, so echoing those values back would store the distributor's
  // configuration as the entity's own. Null keeps the columns untouched, the same way
  // distributorDomain is skipped for non admins.
  private async buildDashboardPayload() {
    if (!this.canEditDashboard) return null;

    const card = this.currentSettings?.dashboard?.card;

    return {
      card: {
        headerColor: this.settingsForm.value.dashboardCardHeaderColor,
        outlineColor: this.settingsForm.value.dashboardCardOutlineColor,
        mimesys: await this.buildCardImagesPayload(
          'dashboardCardMimesysHeaderLogo', 'dashboardCardMimesysBodyImage',
          card?.mimesys?.headerLogoImage, card?.mimesys?.bodyImage),
        fit: await this.buildCardImagesPayload(
          'dashboardCardFitHeaderLogo', 'dashboardCardFitBodyImage',
          card?.fit?.headerLogoImage, card?.fit?.bodyImage),
        depict360: await this.buildCardImagesPayload(
          'dashboardCardDepict360HeaderLogo', 'dashboardCardDepict360BodyImage',
          card?.depict360?.headerLogoImage, card?.depict360?.bodyImage),
      },
      // Whoever cannot change the grant sends null, so the column keeps its value.
      allowConfigure: this.canToggleAllowConfigure
        ? this.settingsForm.get('dashboardAllowConfigure')?.value
        : null,
    };
  }

  // The three cards carry the same two images, so they share one builder. No cleanParams
  // here: it drops the cleared image and then the whole card object, and the backend can
  // only clear a column it actually receives.
  private async buildCardImagesPayload(
    headerLogoControl: string, bodyImageControl: string,
    currentHeaderLogo?: string | null, currentBodyImage?: string | null) {

    return {
      headerLogoImage: await this.resolveCardImage(headerLogoControl, currentHeaderLogo),
      bodyImage: await this.resolveCardImage(bodyImageControl, currentBodyImage),
      headerLogoName: this.actualFilenames[headerLogoControl],
      bodyImageName: this.actualFilenames[bodyImageControl],
    };
  }

  // A file just picked travels as base64; otherwise the stored value is echoed back, with
  // the empty one normalised to null so the backend reads it as "clear this column".
  private async resolveCardImage(controlName: string, currentImage?: string | null): Promise<string | null> {
    if (this.settingsForm.get(controlName)?.value) return await this.toBase64(controlName);
    return currentImage ? currentImage : null;
  }

  setForm(res: Settings) {
    this.currentSettings = res;
    this.settingsForm.patchValue({
      language: res.language,
      loginToken: res.loginToken,
      distributorDomain: res.distributorDomain,
      distributorDomainAllow: res.distributorDomainConfigurable,
      fromMail: res.fromMail,
      fromMailAllow: res.fromMailConfigurable,
      sendPasswordEmail: res.sendPasswordEmail,
      sendPasswordEmailConfigurable: res.sendPasswordEmailConfigurable,
      modalPosition: res.login.form.position,
      modalColor: res.login.form.backgroundColor,
      header: res.login.header.backgroundColor,
      wallpaperBg: res.login.wallpaper.background,
      wallpaperColor: res.login.wallpaper.backgroundColor,
      sidebarBg: res.layout.sidebar.background,
      sidebarColor: res.layout.sidebar.backgroundColor,
      sidebarFontColor: res.layout.sidebar.color,
      logo4Allow: res.logo[4].configurable,
      logo5Allow: res.logo[5].configurable,
      logo6Allow: res.logo[6].configurable,
      dashboardCardHeaderColor: res.dashboard?.card?.headerColor ?? null,
      dashboardCardOutlineColor: res.dashboard?.card?.outlineColor ?? null,
      dashboardAllowConfigure: res.dashboard?.allowConfigure ?? false,
    });
    this.refreshActualNames();
  }

  getFileNameFromPath(_path: String, _file?: string) {
    this.actualFilenames[_file!] = _path.replace(/^.*[\\/]/, '');
  }
  uploadFile(event: Event, _name: string) {
    const element = event.currentTarget as HTMLInputElement;
    let fileList: FileList | null = element.files;

    if (!fileList || fileList.length === 0) return;

    const file = fileList[0];
    const control = this.settingsForm.get(_name);

    if (!control) return;

    // Validar con el servicio centralizado
    if (!this.fileSpecsService.validateAndSetFormControl(_name, file, control, element)) {
      return;
    }

    this.getFileNameFromPath(fileList[0].name.split(' ').join('-'), _name);
    this.toBase64(_name).then(result => {
      switch (_name) {
        case 'logo1':
        case 'logo2':
        case 'logo3':
        case 'logo4':
        case 'logo5':
        case 'logo6':
          Object.values(this.currentSettings!.logo)[Number(_name.slice(-1)) - 1].image = result;
          break;
        case 'wallpaperImage':
          this.currentSettings!.login!.wallpaper!.backgroundImage = result;
          this.loginBackgroundImage = 'url(' + result + ')';
          this.renderLoginLayout(this.settingsForm.get('wallpaperColor')?.value!);
          break;
        case 'sidebarImage':
          this.currentSettings!.layout!.sidebar!.backgroundImage = result;
          this.sideBarBackgroundImage = 'url(' + result + ')';
          this.renderSidebarLayout(this.settingsForm.get('sidebarColor')?.value!);
          break;
        case 'dashboardCardMimesysHeaderLogo':
          this.currentSettings!.dashboard!.card.mimesys.headerLogoImage = result;
          break;
        case 'dashboardCardMimesysBodyImage':
          this.currentSettings!.dashboard!.card.mimesys.bodyImage = result;
          break;
        case 'dashboardCardFitHeaderLogo':
          this.currentSettings!.dashboard!.card.fit.headerLogoImage = result;
          break;
        case 'dashboardCardFitBodyImage':
          this.currentSettings!.dashboard!.card.fit.bodyImage = result;
          break;
        case 'dashboardCardDepict360HeaderLogo':
          this.currentSettings!.dashboard!.card.depict360.headerLogoImage = result;
          break;
        case 'dashboardCardDepict360BodyImage':
          this.currentSettings!.dashboard!.card.depict360.bodyImage = result;
          break;
        default:
          break;
      }
    });
    this.configService.setConfigSettingsValue('settings', this.entity.id, 'backOffice', this.setUpdate());
  }

  clearImage(e: Event, _image?: string | undefined) {
    e.preventDefault();
    delete this.actualFilenames[_image!];
    this.settingsForm.get(_image!)?.patchValue('');
    switch (_image) {
      case 'wallpaperImage':
        this.currentSettings!.login.wallpaper.backgroundImage = '';
        this.loginBackgroundImage = '';
        this.renderLoginLayout(this.settingsForm.get('wallpaperColor')?.value!);
        break;
      case 'sidebarImage':
        this.currentSettings!.layout.sidebar.backgroundImage = '';
        this.sideBarBackgroundImage = '';
        this.renderSidebarLayout(this.settingsForm.get('sidebarColor')?.value!);
        break;
      case 'logo1':
      case 'logo2':
      case 'logo3':
      case 'logo4':
      case 'logo5':
      case 'logo6':
        Object.values(this.currentSettings!.logo)[Number(_image.slice(-1)) - 1].image = '';
        this.fileExtError[_image] = false;
        this.maxFileSizeExceed[_image] = false;
        break;
      case 'dashboardCardMimesysHeaderLogo':
        this.currentSettings!.dashboard!.card.mimesys.headerLogoImage = '';
        this.fileExtError[_image] = false;
        this.maxFileSizeExceed[_image] = false;
        break;
      case 'dashboardCardMimesysBodyImage':
        this.currentSettings!.dashboard!.card.mimesys.bodyImage = '';
        this.fileExtError[_image] = false;
        this.maxFileSizeExceed[_image] = false;
        break;
      case 'dashboardCardFitHeaderLogo':
        this.currentSettings!.dashboard!.card.fit.headerLogoImage = '';
        this.fileExtError[_image] = false;
        this.maxFileSizeExceed[_image] = false;
        break;
      case 'dashboardCardFitBodyImage':
        this.currentSettings!.dashboard!.card.fit.bodyImage = '';
        this.fileExtError[_image] = false;
        this.maxFileSizeExceed[_image] = false;
        break;
      case 'dashboardCardDepict360HeaderLogo':
        this.currentSettings!.dashboard!.card.depict360.headerLogoImage = '';
        this.fileExtError[_image] = false;
        this.maxFileSizeExceed[_image] = false;
        break;
      case 'dashboardCardDepict360BodyImage':
        this.currentSettings!.dashboard!.card.depict360.bodyImage = '';
        this.fileExtError[_image] = false;
        this.maxFileSizeExceed[_image] = false;
        break;
      default:
        break;
    }
    this.configService.setConfigSettingsValue('settings', this.entity.id, 'backOffice', this.setUpdate());
  }

  showImage(e: Event, _image?: string) {
    e.preventDefault();
    if (_image!.startsWith("http")) {
      var image = new Image();
      image.src = _image!;
      var w = window.open("", "_blank");

      w!.document.write(image.outerHTML);
      w!.document.body.style.background = '#000';
    } else {
      var w = window.open("", "_blank");
      w!.document.write("<img src='" + _image! + "' />");
      w!.document.body.style.background = '#000';
    }

  }

  someFileSizeInvalid = () => {
    return Object.values(this.maxFileSizeExceed).some(value => value === true)
  }
  someFileNameInvalid = () => {
    return Object.values(this.badFileNaming).some(value => value === true)
  }
  someFileExtInvalid = () => {
    return Object.values(this.fileExtError).some(value => value === true)
  }

  extractFileName = (_image: string) => {
    let filePath = '';
    switch (_image) {
      case 'wallpaperImage':
        filePath = this.currentSettings!.login.wallpaper.backgroundImage;
        break;
      case 'sidebarImage':
        filePath = this.currentSettings!.layout.sidebar.backgroundImage;
        break;
      case 'logo1':
      case 'logo2':
      case 'logo3':
      case 'logo4':
      case 'logo5':
      case 'logo6':
        filePath = Object.values(this.currentSettings!.logo)[Number(_image.slice(-1)) - 1].image;
        break;
      default:
        break;
    }
    this.actualFilenames[_image] = filePath!.split(/(\\|\/)/g).pop()!;
    return filePath!.split(/(\\|\/)/g).pop();
  }

  updateImage = (_image: string) => {
    return this.currentSettings?.login!.wallpaper!.backgroundImage! + '?' + new Date().getTime()
  }

  recoverImage = (_image: string) => {
    Object.values(this.currentSettings!.logo)[Number(_image.slice(-1)) - 1].image = Object.values(this.currentSettings!.logo)[Number(_image.slice(-1)) - 1].default!;
    this.getFileNameFromPath(Object.values(this.currentSettings!.logo)[Number(_image.slice(-1)) - 1].default!, _image);
    /*const inputElement = document.getElementById(`${_image}-${this.entity.id}`) as HTMLInputElement;
    inputElement.value = '';*/
    this.setForm(this.currentSettings!);
  }

  refreshActualNames = () => {
    this.actualFilenames['wallpaperImage'] = this.currentSettings!.login.wallpaper.backgroundImage?.split(/(\\|\/)/g).pop()!;
    this.actualFilenames['sidebarImage'] = this.currentSettings!.layout.sidebar.backgroundImage?.split(/(\\|\/)/g).pop()!;
    this.logos.map(logo => this.actualFilenames[logo] = Object.values(this.currentSettings!.logo)[Number(logo.slice(-1)) - 1].image?.split(/(\\|\/)/g).pop()!);
  }

}
